import { useEffect, useMemo, useState } from "react";

import { Box, Button, MenuItem, TextField } from "@mui/material";
import { useSnackbar } from "notistack";
import { useSelector } from "react-redux";

import BasicModal from "../../components/modals/BasicModal";
import DateInput from "../../components/inputs/DateInput";
import DateTimeInput from "../../components/inputs/DateTimeInput";
import Flx from "../../components/layout/Flx";
import Txt from "../../components/typography/Txt";
import { closingCoverageApi } from "../../api/closingCoverageApi";
import { C } from "./coverageUtils";

const Field = ({ label, children }) => (
  <Box>
    <Txt sx={{ fontSize: 12, fontWeight: 600, color: C.sub, mb: 0.5 }}>{label}</Txt>
    {children}
  </Box>
);

// Create or edit an out-of-office entry. Pass `entry` to edit; omit to create.
const OOOFormModal = ({ show, onClose, entry, onSaved }) => {
  const { enqueueSnackbar } = useSnackbar();
  const users = useSelector((state) => state.users);

  const [allDay, setAllDay] = useState(true);
  const [start, setStart] = useState(null);
  const [end, setEnd] = useState(null);
  const [reason, setReason] = useState("");
  const [backupEmail, setBackupEmail] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const isEdit = Boolean(entry?._id);

  // (Re)seed the form whenever the modal opens.
  useEffect(() => {
    if (!show) return;
    setAllDay(entry ? !!entry.allDay : true);
    setStart(entry?.start || null);
    setEnd(entry?.end || null);
    setReason(entry?.reason || "");
    setBackupEmail(entry?.backupEmail || "");
    setNote(entry?.note || "");
  }, [show, entry]);

  const backupOptions = useMemo(
    () =>
      (Array.isArray(users) ? users : [])
        .filter((u) => u?.emailAddress)
        .map((u) => ({
          email: u.emailAddress,
          label: u.fullName || u.emailAddress,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [users],
  );

  const valid =
    start && end && new Date(end).getTime() >= new Date(start).getTime();

  const handleSave = async () => {
    if (!valid) {
      enqueueSnackbar("Pick a start and end (end must be on/after start).", {
        variant: "warning",
      });
      return;
    }
    setSaving(true);
    const body = {
      start,
      end,
      allDay,
      reason: reason || undefined,
      backupEmail: backupEmail || undefined,
      note: note || undefined,
    };
    try {
      const saved = isEdit
        ? await closingCoverageApi.updateOoo(entry._id, body)
        : await closingCoverageApi.createOoo(body);
      enqueueSnackbar(isEdit ? "Coverage updated." : "Marked out of office.", {
        variant: "success",
      });
      onSaved?.(saved);
      onClose?.();
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        err?.response?.data?.error ||
        "Could not save the entry.";
      enqueueSnackbar(String(detail), { variant: "error" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <BasicModal
      show={show}
      onClose={onClose}
      maxWidth="xs"
      title={isEdit ? "Edit out of office" : "Mark out of office"}
    >
      <Flx column g={2} sx={{ pt: 1 }}>
        {/* Duration mode — segmented toggle (clearer than a bare switch). */}
        <Flx
          ac
          sx={{
            alignSelf: "flex-start",
            p: 0.4,
            borderRadius: "10px",
            background: C.bg,
            border: `1px solid ${C.line}`,
          }}
        >
          {[
            { v: true, label: "All day" },
            { v: false, label: "Specific times" },
          ].map((opt) => (
            <Box
              key={opt.label}
              onClick={() => setAllDay(opt.v)}
              sx={{
                px: 1.6,
                py: 0.7,
                borderRadius: "8px",
                cursor: "pointer",
                fontSize: 12.5,
                fontWeight: 600,
                color: allDay === opt.v ? "#fff" : C.sub,
                background: allDay === opt.v ? C.accent : "transparent",
                transition: "background 0.12s, color 0.12s",
                "&:hover": { color: allDay === opt.v ? "#fff" : C.ink },
              }}
            >
              {opt.label}
            </Box>
          ))}
        </Flx>

        <Flx g={1.5} wrap>
          <Field label="Start">
            {allDay ? (
              <DateInput value={start} onChange={setStart} onAccept={setStart} />
            ) : (
              <DateTimeInput value={start} onChange={setStart} onAccept={setStart} />
            )}
          </Field>
          <Field label="End">
            {allDay ? (
              <DateInput value={end} onChange={setEnd} onAccept={setEnd} />
            ) : (
              <DateTimeInput value={end} onChange={setEnd} onAccept={setEnd} />
            )}
          </Field>
        </Flx>

        <Field label="Reason">
          <TextField
            size="small"
            fullWidth
            placeholder="PTO, Sick, Appointment…"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </Field>

        <Field label="Backup (optional)">
          <TextField
            select
            size="small"
            fullWidth
            value={backupEmail}
            onChange={(e) => setBackupEmail(e.target.value)}
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {backupOptions.map((o) => (
              <MenuItem key={o.email} value={o.email} sx={{ fontSize: 13 }}>
                {o.label}
              </MenuItem>
            ))}
          </TextField>
        </Field>

        <Field label="Note (optional)">
          <TextField
            size="small"
            fullWidth
            multiline
            minRows={2}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </Field>

        <Flx ac end g={1} sx={{ mt: 0.5 }}>
          <Button variant="text" color="gray" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={saving || !valid}>
            {isEdit ? "Save" : "Mark out"}
          </Button>
        </Flx>
      </Flx>
    </BasicModal>
  );
};

export default OOOFormModal;
